import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Check, Settings as SettingsIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ResponsiveDialog } from '@/components/ui/responsive-dialog';
import { cn } from '@/lib/utils';
import { isNative } from '@/lib/api-base';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'cookie-consent-v1';

interface ConsentPrefs {
  necessary: true; // always
  analytics: boolean;
  marketing: boolean;
  date: number;
}

function loadPrefs(): ConsentPrefs | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.date === 'number') return parsed as ConsentPrefs;
    return null;
  } catch {
    return null;
  }
}

function savePrefs(p: ConsentPrefs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  // Dispatch a custom event so other modules (analytics loader, marketing
  // pixel, etc) can react without prop-drilling.
  window.dispatchEvent(new CustomEvent('cookie-consent', { detail: p }));
}

export function CookieConsent() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Skip the cookie banner inside the Capacitor app — native apps
    // don't fall under EU cookie law (no third-party cookies, sandboxed
    // localStorage). Analytics consent for native users is handled by
    // OS-level privacy settings.
    if (isNative()) return;
    // Never inside an iframe. An embedded tour or demo is third-party
    // content on somebody else's page, and that page owns the consent
    // conversation — ours would be a second banner covering the very
    // thing the visitor came to look at. It also couldn't be dismissed on
    // the demo surface, whose interaction blocker sits above it. This is
    // the same reason a client embedding a tour on their own site should
    // never see our banner appear inside their page.
    let embedded = false;
    try {
      embedded = window.self !== window.top;
    } catch {
      // Cross-origin parent throws on access — that IS an embed.
      embedded = true;
    }
    if (embedded || window.location.pathname.startsWith('/demo/')) return;
    // Defer to next tick so we don't flash on first paint
    const t = setTimeout(() => {
      const prefs = loadPrefs();
      if (!prefs) setShow(true);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  const acceptAll = () => {
    savePrefs({ necessary: true, analytics: true, marketing: true, date: Date.now() });
    setShow(false);
  };
  const acceptOnlyNecessary = () => {
    savePrefs({ necessary: true, analytics: false, marketing: false, date: Date.now() });
    setShow(false);
  };
  const saveSelection = () => {
    savePrefs({ necessary: true, analytics, marketing, date: Date.now() });
    setDetailsOpen(false);
    setShow(false);
  };

  return (
    <>
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
            className="fixed bottom-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] sm:w-auto sm:max-w-lg z-[100]"
          >
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]/95 backdrop-blur-md shadow-2xl p-5">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-[var(--color-primary)]/12 border border-[var(--color-primary)]/25 flex items-center justify-center text-[var(--color-primary)] shrink-0">
                  <Cookie className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm tracking-tight text-[var(--color-text)]">
                    {t('componentsMisc:cookieConsent.title')}
                  </h3>
                  <p className="text-xs text-[var(--color-text-muted)] mt-1 leading-relaxed">
                    {t('componentsMisc:cookieConsent.description')}{' '}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noreferrer"
                      className="text-[var(--color-primary)] hover:underline font-medium"
                    >
                      {t('componentsMisc:cookieConsent.privacyPolicy')}
                    </a>
                  </p>
                </div>
                <button
                  onClick={acceptOnlyNecessary}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors p-0.5 -mt-1"
                  aria-label={t('componentsMisc:cookieConsent.closeAriaLabel')}
                  title={t('componentsMisc:cookieConsent.close')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Button variant="ghost" size="sm" onClick={() => setDetailsOpen(true)} className="text-xs">
                  <SettingsIcon className="w-3.5 h-3.5 mr-1.5" />
                  {t('componentsMisc:cookieConsent.customize')}
                </Button>
                <Button variant="secondary" size="sm" onClick={acceptOnlyNecessary} className="text-xs">
                  {t('componentsMisc:cookieConsent.necessaryOnly')}
                </Button>
                <Button variant="primary" size="sm" onClick={acceptAll} className="text-xs">
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                  {t('componentsMisc:cookieConsent.acceptAll')}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ResponsiveDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        title={t('componentsMisc:cookieConsent.prefsTitle')}
        description={t('componentsMisc:cookieConsent.prefsDescription')}
        size="md"
        footer={
          <div className="flex w-full gap-2 justify-end">
            <Button variant="ghost" onClick={() => setDetailsOpen(false)}>{t('componentsMisc:cookieConsent.cancel')}</Button>
            <Button variant="primary" onClick={saveSelection}>
              <Check className="w-4 h-4 mr-1.5" />
              {t('componentsMisc:cookieConsent.savePrefs')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <ConsentRow
            label={t('componentsMisc:cookieConsent.rows.necessary.label')}
            description={t('componentsMisc:cookieConsent.rows.necessary.description')}
            checked
            disabled
            onChange={() => {}}
          />
          <ConsentRow
            label={t('componentsMisc:cookieConsent.rows.analytics.label')}
            description={t('componentsMisc:cookieConsent.rows.analytics.description')}
            checked={analytics}
            onChange={setAnalytics}
          />
          <ConsentRow
            label={t('componentsMisc:cookieConsent.rows.marketing.label')}
            description={t('componentsMisc:cookieConsent.rows.marketing.description')}
            checked={marketing}
            onChange={setMarketing}
          />
        </div>
      </ResponsiveDialog>
    </>
  );
}

function ConsentRow({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <label
      className={cn(
        'flex items-start gap-3 p-3 rounded-xl border transition-colors cursor-pointer',
        disabled
          ? 'border-[var(--color-border)] bg-[var(--color-surface-elevated)] cursor-not-allowed opacity-90'
          : checked
          ? 'border-[var(--color-primary)]/40 bg-[var(--color-primary)]/5 hover:bg-[var(--color-primary)]/10'
          : 'border-[var(--color-border)] bg-[var(--color-surface-elevated)] hover:border-[var(--color-primary)]/30',
      )}
    >
      <div className="relative pt-0.5">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="peer absolute opacity-0 w-4 h-4"
        />
        <div
          className={cn(
            'flex items-center justify-center w-4 h-4 rounded border transition-all',
            checked
              ? 'bg-[var(--color-primary)] border-[var(--color-primary)]'
              : 'bg-[var(--color-base)] border-[var(--color-border)] peer-hover:border-[var(--color-primary)]/50',
          )}
        >
          {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-[var(--color-text)]">{label}</span>
          {disabled && (
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[var(--color-primary)] bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/20 rounded px-1.5 py-0.5">
              {t('componentsMisc:cookieConsent.alwaysActive')}
            </span>
          )}
        </div>
        <p className="text-xs text-[var(--color-text-muted)] mt-1 leading-relaxed">{description}</p>
      </div>
    </label>
  );
}
